import { useId } from 'react';

/**
 * One tileable SVG band of parallel waves. Split out so the desktop and mobile
 * variants can share identical path geometry. `patternId` must be unique per
 * rendered instance; `className` toggles per-breakpoint visibility.
 */
function WaveSvg({
  patternId,
  height,
  spacing,
  amp,
  className,
}: {
  patternId: string;
  height: number;
  spacing: number;
  amp: number;
  className: string;
}) {
  return (
    <svg
      className={`block w-full ${className}`}
      width="100%"
      height={height}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <pattern id={patternId} x="0" y="0" width="280" height={height} patternUnits="userSpaceOnUse">
          {/* One row every `spacing`px, plus one extra so waves fill the whole
              band height (crests/troughs clip at the edges). Control offset
              `amp` gives each line a deep, rounded curve (peak deviation =
              amp/2); crests reach close to the line above in the humps. */}
          {Array.from({ length: Math.ceil(height / spacing) + 1 }).map((_, i) => {
            const y = i * spacing;
            return (
              <path
                key={i}
                d={`M0 ${y} q70 ${-amp} 140 0 t140 0`}
                fill="none"
                stroke="var(--vibe-color-primary)"
                strokeWidth={1.5}
              />
            );
          })}
        </pattern>
      </defs>
      <rect width="100%" height={height} fill={`url(#${patternId})`} />
    </svg>
  );
}

/**
 * Decorative band of pronounced, consistent-amplitude parallel waves — the
 * same motif that sits under the hero. Not a registered component (no
 * contentSchema, never in registry.ts / components/index.ts), so the schema
 * linter skips it.
 *
 * `className` styles the wrapper (background color, width, margins, z-index);
 * `height` sets the desktop band height. The top crests and bottom troughs clip
 * at the band edges, so the wave bleeds at the top and breaks at the bottom.
 * Each instance gets unique <pattern> ids so multiple bands can coexist.
 *
 * `spacing` is the vertical distance between wave baselines and `amp` is the
 * quadratic control offset (peak deviation = amp/2). They default to the
 * homepage-band geometry (18 / 30); tighten them together on a per-instance
 * basis to pack the waves more closely while keeping the crest-to-line gap.
 *
 * Responsive: below `lg` (i.e. mobile AND tablet) the band renders a true 2×
 * reduction — `height`, `spacing` AND `amp` are all halved together. Halving the
 * amplitude too keeps the crest-depth-to-spacing ratio identical to desktop, so
 * the waves stay distinct and evenly packed right up to the top edge instead of
 * overlapping into gaps at the smaller scale. The reduced geometry now extends
 * through the tablet range; desktop (≥ lg) geometry is unchanged.
 *
 * `mobileHeight` / `mobileSpacing` / `mobileAmp` (all optional) override the
 * mobile-only breakpoint (< md) geometry with explicit raw values instead of the
 * auto-halved defaults. Use them when the phone band should match a DIFFERENT
 * band's mobile look rather than a straight halving of this instance's desktop
 * geometry (e.g. a tall closing band that should read like the short hero band
 * on phones). Any left unset falls back to the halved desktop value, so the
 * defaults leave every existing usage pixel-identical. Tablet (md–lg) and
 * desktop (≥ lg) are never affected by these.
 */
export function WaveBand({
  className = '',
  height = 100,
  spacing = 18,
  amp = 30,
  mobileHeight,
  mobileSpacing,
  mobileAmp,
}: {
  className?: string;
  height?: number;
  spacing?: number;
  amp?: number;
  mobileHeight?: number;
  mobileSpacing?: number;
  mobileAmp?: number;
}) {
  // useId() can contain ":" which is awkward inside url(#…); strip it.
  const base = `wave-${useId().replace(/:/g, '')}`;
  // Mobile (< md) geometry: explicit overrides when provided, else the
  // auto-halved desktop geometry (same as the tablet band).
  const mHeight = mobileHeight ?? height / 2;
  const mSpacing = mobileSpacing ?? spacing / 2;
  const mAmp = mobileAmp ?? amp / 2;
  return (
    <div className={`leading-[0] ${className}`} aria-hidden="true">
      {/* Desktop (≥ lg): full geometry. */}
      <WaveSvg patternId={`${base}-d`} height={height} spacing={spacing} amp={amp} className="hidden lg:block" />
      {/* Tablet (md–lg): spacing, height and amplitude all halved (true 2× reduction). */}
      <WaveSvg
        patternId={`${base}-t`}
        height={height / 2}
        spacing={spacing / 2}
        amp={amp / 2}
        className="hidden md:block lg:hidden"
      />
      {/* Mobile (< md): halved geometry by default, or the explicit mobile overrides. */}
      <WaveSvg patternId={`${base}-m`} height={mHeight} spacing={mSpacing} amp={mAmp} className="md:hidden" />
    </div>
  );
}
